import React from "react";

// The Control Room's time controls (report schedule, Business hours, Meeting
// hours), in one place so every card uses the same pickers. Moved here from
// pages/ControlRoom.js as they were; `ariaLabel` (optional) names a trigger
// that sits under a visual label.

export const DAYS = [
  ["MON", "Monday"],
  ["TUE", "Tuesday"],
  ["WED", "Wednesday"],
  ["THU", "Thursday"],
  ["FRI", "Friday"],
  ["SAT", "Saturday"],
  ["SUN", "Sunday"],
];
export const TIMES = Array.from({ length: 24 * 4 }, (_, i) => {
  const h = String(Math.floor(i / 4)).padStart(2, "0");
  const m = String((i % 4) * 15).padStart(2, "0");
  return `${h}:${m}`;
});
/* ---------- timezone utils + dropdown styled like Team Directory ---------- */
export function getSupportedTimeZones() {
  if (typeof Intl?.supportedValuesOf === "function") {
    try {
      return Intl.supportedValuesOf("timeZone");
    } catch {}
  }
  return [
    "Etc/UTC",
    "America/Los_Angeles",
    "America/Denver",
    "America/Chicago",
    "America/New_York",
    "America/Phoenix",
    "America/Toronto",
    "America/Mexico_City",
    "America/Sao_Paulo",
    "Europe/London",
    "Europe/Berlin",
    "Europe/Paris",
    "Europe/Madrid",
    "Africa/Johannesburg",
    "Asia/Dubai",
    "Asia/Kolkata",
    "Asia/Singapore",
    "Asia/Tokyo",
    "Australia/Sydney",
    "Pacific/Auckland",
  ];
}
function getOffsetLabel(tz) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(new Date());
    const off = parts.find((p) => p.type === "timeZoneName")?.value || "";
    return off.replace("GMT", "UTC");
  } catch {
    return "UTC±00:00";
  }
}
export function FancyTZSelect({ value, onChange, options, placeholder, width, ariaLabel }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [active, setActive] = React.useState(0);
  const wrapRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const data = React.useMemo(() => {
    return (options || []).map((tz) => {
      const off = getOffsetLabel(tz);
      const name = tz.replaceAll("_", " ");
      return {
        value: tz,
        label: name,
        offset: off,
        search: `${tz.toLowerCase()} ${name.toLowerCase()} ${off.toLowerCase()}`,
      };
    });
  }, [options]);

  const selected = React.useMemo(
    () => data.find((d) => d.value === value) || null,
    [data, value],
  );
  const filtered = React.useMemo(() => {
    if (!q.trim()) return data;
    const k = q.trim().toLowerCase();
    return data.filter((d) => d.search.includes(k));
  }, [data, q]);

  React.useEffect(() => {
    function onDoc(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  React.useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
    else {
      setQ("");
      setActive(0);
    }
  }, [open]);

  function commit(val) {
    onChange?.(val);
    setOpen(false);
    setQ("");
  }

  return (
    <div
      className={`select ${open ? "open" : ""}`}
      ref={wrapRef}
      // width matters inside a flex row, where this box shrink-wraps its label
      // and re-flows its neighbours every time the value changes. In a grid cell
      // it fills the cell as before.
      style={{ position: "relative", width }}
    >
      <button
        type="button"
        className="selectTrigger"
        data-placeholder={selected ? "false" : "true"}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={
          ariaLabel
            ? `${ariaLabel}, ${selected ? selected.label : placeholder || "Timezone"}`
            : undefined
        }
        style={{ width: "100%" }}
        title={
          selected
            ? `${selected.label} (${selected.offset})`
            : "Select a valid IANA time zone"
        }
      >
        <span className="selectValue">
          {selected
            ? `${selected.label} (${selected.offset})`
            : placeholder || "Timezone"}
        </span>
        <span className="selectCaret" aria-hidden />
      </button>

      <div
        className="selectPanel"
        role="listbox"
        style={{ maxHeight: 320, overflow: "auto", left: 0, right: 0 }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "ArrowDown")
            setActive((i) => Math.min(i + 1, filtered.length - 1));
          if (e.key === "ArrowUp") setActive((i) => Math.max(i - 1, 0));
          if (e.key === "Enter") {
            const pick = filtered[active];
            if (pick) commit(pick.value);
          }
        }}
      >
        <div style={{ padding: 6 }}>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            placeholder="Search time zones"
            aria-label="Search time zones"
            className="inputFancy"
            style={{ width: "100%", outline: "none" }}
          />
        </div>
        <div className="scrollable" style={{ padding: 6, paddingTop: 0 }}>
          {filtered.length === 0 ? (
            <div className="muted" style={{ padding: 10 }}>
              No matches
            </div>
          ) : (
            filtered.map((opt, i) => {
              const isSel = opt.value === value;
              const isActive = i === active;
              return (
                <div
                  key={opt.value}
                  role="option"
                  aria-selected={isSel}
                  className={`selectOption ${isSel ? "selected" : ""} ${
                    isActive ? "active" : ""
                  }`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => commit(opt.value)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <span>{opt.label}</span>
                  <span className="muted" style={{ opacity: 0.9 }}>
                    {opt.offset}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- tiny generic select with same style (for Day/Time) ---------- */
export function FancyMiniSelect({ value, onChange, options, width = 160, ariaLabel }) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  React.useEffect(() => {
    function onDoc(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const current = options.find((o) => o.value === value)?.label || value;

  return (
    <div
      className={`select ${open ? "open" : ""}`}
      ref={wrapRef}
      style={{ position: "relative" }}
    >
      <button
        type="button"
        className="selectTrigger"
        onClick={() => setOpen((v) => !v)}
        style={{ width }}
        title={current}
        aria-label={ariaLabel ? `${ariaLabel}, ${current}` : undefined}
      >
        <span className="selectValue">{current}</span>
        <span className="selectCaret" aria-hidden />
      </button>
      <div
        className="selectPanel"
        role="listbox"
        style={{ maxHeight: 280, overflow: "auto" }}
      >
        <div className="scrollable" style={{ padding: 6 }}>
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`selectOption ${
                opt.value === value ? "selected" : ""
              }`}
              role="option"
              aria-selected={opt.value === value}
              onClick={() => {
                onChange?.(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
export function DayPills({ value = [], onChange }) {
  const sel = new Set(value);
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {DAYS.map(([v, label]) => {
        const active = sel.has(v);
        return (
          <button
            key={v}
            type="button"
            onClick={() => {
              const next = new Set(sel);
              if (active) next.delete(v);
              else next.add(v);
              onChange(Array.from(next));
            }}
            className={`chip crOptionCard--inline ${active ? "ok" : ""}`}
            style={{ cursor: "pointer", fontSize: 12 }}
            aria-pressed={active}
            title={label}
          >
            {v}
          </button>
        );
      })}
    </div>
  );
}
